<template>
  <div v-if="open" class="relative w-full max-h-auto alert-box" :class="[errorType(), zOrderClass]" role="alert" ref="alertContainerRef">
    <div class="flex py-4 container-large">
      <div class="flex justify-between w-full items-top">
        <div>
          <div class="flex items-center">
            <!-- Icons -->
            <svg v-if="outlineIcon" class="alert-icon-outline" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8H12.01M12 11V16M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#2C2827" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

            <svg v-else class="alert-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM13 7.55556C13 7.00327 12.5523 6.55556 12 6.55556C11.4477 6.55556 11 7.00327 11 7.55556V13.1111C11 13.6634 11.4477 14.1111 12 14.1111C12.5523 14.1111 13 13.6634 13 13.1111V7.55556ZM12 15.4444C11.4477 15.4444 11 15.8922 11 16.4444C11 16.9967 11.4477 17.4444 12 17.4444H12.0111C12.5634 17.4444 13.0111 16.9967 13.0111 16.4444C13.0111 15.8922 12.5634 15.4444 12.0111 15.4444H12Z"
                fill="#2C2827" />
            </svg>

            <!-- Heading -->
            <h2 class="alert-heading">{{ title }}</h2>
          </div>
          <!-- Text -->
          <p class="alert-text" :class="setTextFullWidth ? '' : 'max-w-2xl'">
            {{ text }} <a v-if="showLink" :href="linkUrl" :target="linkTarget" class="alert-link arrow-link-forward" :class="[{ 'arrow-link-forward--white': type === 'critical' }]">{{ linkText }}</a>
          </p>
        </div>

        <!-- Close button -->
        <button v-if="showClose === true || showClose === 'true'" @click.prevent="close" class="alert-close-button">
          <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 8.182L18.182 0 20 1.818 1.818 20 0 18.182 8.182 10 0 1.818 1.818 0 10 8.182zM18.182 20l-6.73-6.73 1.819-1.817L20 18.182 18.182 20z" fill="#2C2827" />
          </svg>
          <span>{{ closeText }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import cookieHelper from '../../../helpers/cookies'

export default {
  name: 'ellevio-web-alert',
  props: {
    outlineIcon: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    setTextFullWidth: {
      type: Boolean,
      default: false
    },
    iconSrc: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'information',
      validator(value) {
        // The value must match one of these strings
        return ['information', 'softWarning', 'warning', 'critical'].includes(value)
      }
    },
    cookieId: {
      type: String,
      default: 'default'
    },
    showClose: {
      type: Boolean,
      default: false
    },
    closeText: {
      type: String,
      default: 'Stäng'
    },
    linkUrl: {
      type: String,
      default: ''
    },
    linkTarget: {
      type: String,
      default: '_self',
      validator(value) {
        // The value must match one of these strings
        return ['_self', '_blank'].includes(value)
      }
    },
    linkText: {
      type: String,
      default: ''
    },
    scrollbarWidth: {
      type: Number,
      default: 0
    },
    desktopMenuIsOpen: {
      type: Boolean,
      default: false
    },
    zOrderSmaller: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      closed: false
    }
  },
  mounted() {
    const doc = document.documentElement
    if (this.open) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            const contentBoxSize = entry.contentBoxSize[0]
            doc.style.setProperty('--alert-height', contentBoxSize.blockSize + 'px')
          }
        }
      })

      const alertBox = document.querySelector('.alert-box')
      resizeObserver.observe(alertBox)
    }
  },
  computed: {
    open() {
      return !cookieHelper.getCookie(this.renderedCookieName) && !this.closed
    },
    showLink() {
      return this.linkUrl
    },
    renderedCookieName() {
      return `EllevioAlertMessage_${this.cookieId}`
    },
    zOrderClass() {
      return this.zOrderSmaller ? 'z-auto' : 'z-50'
    }
  },
  methods: {
    close() {
      const doc = document.documentElement
      doc.style.setProperty('--alert-height', '0px')

      this.closed = true
      cookieHelper.setCookie(this.renderedCookieName, 'closed for 1 day', 1)
    },
    errorType() {
      return `alert-${this.type}` || 'alert-information'
    }
  },
  watch: {
    desktopMenuIsOpen(isOpen) {
      // Prevent alert from jumping when mega menu is open
      if (isOpen) {
        this.$refs.alertContainerRef.style.paddingRight = `${this.scrollbarWidth}px`
      } else {
        this.$refs.alertContainerRef.style.paddingRight = `0`
      }
    }
  }
}
</script>

<style scoped>
.alert-icon {
  @apply relative w-6 h-6 mt-0 mr-2 inline shrink-0;
}
.alert-icon-outline {
  @apply relative mt-0 mr-2 inline shrink-0;
}
.alert-heading {
  @apply text-base lg-rem:text-lg leading-6 font-futuraEfDemiBold max-w-2xl;
}
.alert-text {
  @apply mt-1 text-base lg-rem:text-lg leading-6 inline-block ml-8;
}
.alert-link {
  @apply text-base lg-rem:text-lg leading-6 focus:outline-offset-2;
}
.alert-close-button {
  @apply flex self-start gap-2 text-base mt-1 lg-rem:mt-0 lg-rem:text-lg items-center ml-6 lg-rem:mb-2 focus:outline-dotted focus:outline-newColors-gray-content focus:outline-offset-4 focus:outline-2 rounded-sm;
}
.alert-close-button svg {
  @apply w-4 h-4;
}
.alert-close-button span {
  /* Hide button text on mobile, but show it for screen readers. */
  @apply sr-only lg-rem:not-sr-only;
}

/*--- CRITICAL ---*/
/* Background color */
.alert-critical {
  @apply bg-newColors-red-negative;
}

/* Text color */
.alert-critical,
.alert-critical .alert-heading,
.alert-critical .alert-link {
  @apply text-newColors-gray-base;
}

/* Focus outline color */
.alert-critical .alert-link,
.alert-critical .alert-close-button {
  @apply focus:outline-newColors-gray-base;
}
.alert-critical .alert-close-button {
  @apply focus:outline-newColors-gray-base;
}

/* Icon color */
.alert-critical svg path,
.alert-critical .alert-close-button svg {
  @apply fill-newColors-gray-base;
}
/*--- end CRITICAL---*/

/*--- WARNING ---*/
/* Background color */
.alert-warning {
  @apply bg-newColors-red-negativeB;
}

/* Heading color */
.alert-heading {
  @apply text-newColors-red-negative;
}

/* Icon color */
.alert-warning .alert-icon path {
  @apply fill-newColors-red-negative;
}
/*--- end WARNING---*/

/*--- SOFT WARNING ---*/
/* Background color */
.alert-softWarning {
  @apply bg-newColors-orange-signatureB;
}

/* Heading color */
.alert-heading {
  @apply text-newColors-gray-content;
}

/* Icon color */
.alert-softWarning .alert-icon path {
  @apply fill-newColors-orange-signatureA;
}
/*--- end SOFT WARNING---*/

/*--- INFORMATION ---*/
/* Background color */
.alert-information {
  @apply bg-newColors-green-primaryB;
}

/* Heading color */
.alert-information .alert-heading {
  @apply text-newColors-green-primaryA;
}

/* Icon color */
.alert-information .alert-icon path {
  @apply fill-newColors-green-primaryA;
}
/*--- end INFORMATION---*/
</style>
